/**
 * Timezone detection utilities
 * Provides browser-based timezone detection for automatic user timezone setup
 */

/**
 * Detect the user's timezone using browser APIs
 * @returns IANA timezone string (e.g., "America/Los_Angeles") or null if detection fails
 */
export function detectUserTimezone(): string | null {
  try {
    // Use Intl.DateTimeFormat to get the user's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    if (timezone && typeof timezone === 'string' && timezone.length > 0) {
      console.log('[TimezoneUtils] Detected timezone:', timezone)
      return timezone
    }

    console.warn('[TimezoneUtils] Timezone detection returned empty value')
    return null
  } catch (error) {
    console.error('[TimezoneUtils] Error detecting timezone:', error)
    return null
  }
}

/**
 * Map detected timezone to a user-friendly display name
 * @param timezone - IANA timezone string
 * @returns User-friendly timezone name
 */
export function getTimezoneFriendlyName(timezone: string): string {
  const timezoneMap: Record<string, string> = {
    'America/New_York': 'Eastern Time (US & Canada)',
    'America/Chicago': 'Central Time (US & Canada)',
    'America/Denver': 'Mountain Time (US & Canada)',
    'America/Los_Angeles': 'Pacific Time (US & Canada)',
    'America/Anchorage': 'Alaska Time',
    'Pacific/Honolulu': 'Hawaii Time',
    'Europe/London': 'London',
    'Europe/Paris': 'Paris',
    'Asia/Tokyo': 'Tokyo',
  }

  return timezoneMap[timezone] || timezone
}

/**
 * Validate if a timezone string is valid
 * @param timezone - Timezone string to validate
 * @returns True if valid, false otherwise
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    // Try to create a date formatter with the timezone
    Intl.DateTimeFormat('en-US', { timeZone: timezone })
    return true
  } catch (error) {
    return false
  }
}
